import React, { useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import InventoryMobileCard from './InventoryMobileCard';
import ReservedStockIndicator from './ReservedStockIndicator';
import LeftoverIndicator from './LeftoverIndicator';
import { Eye, ArrowUpDown, Recycle } from 'lucide-react';
import { useMediaQuery } from '../hooks/useMediaQuery';

// Responsive inventory list component (read-only, stock changes via warehouse movements)
const InventoryList = React.memo(({
  inventory,
  loading,
  sort,
  onSort,
  onClearAllFilters,
  debouncedGlobalSearch,
  onViewHistory,
  onViewLeftovers,
  onRefresh,
  selectedClientId = null,
  selectedClient = null,
  viewMode = 'finished'
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const headCells = React.useMemo(() => {
    const cells = [
      { id: 'name', label: '项目名称', sortable: true },
      { id: 'product_id', label: '项目编号', sortable: true },
      { id: 'category', label: '分类', sortable: true },
      { id: 'specification', label: '规格', sortable: true },
    ];

    // Add client column for global view only
    if (!selectedClientId || selectedClientId === 'global') {
      cells.push({ id: 'client_name', label: '客户', sortable: true });
    }

    cells.push(
      {
        id: 'remaining_quantity',
        label: selectedClientId && selectedClientId !== 'global'
          ? `客户库存 (${selectedClient?.name || selectedClientId})`
          : '库存水平',
        sortable: true
      },
      { id: 'leftovers', label: '余料', sortable: false }
    );

    // Raw material specific columns
    if (viewMode === 'raw_material') {
      cells.push({ id: 'purchase_pending_quantity', label: '采购未收', sortable: false });
    }

    cells.push(
      { id: 'tip_diameter', label: '直径', sortable: true },
      { id: 'location', label: '位置', sortable: true },
      { id: 'status', label: '状态', sortable: false },
      { id: 'actions', label: '操作', sortable: false, width: 160 }
    );

    return cells;
  }, [selectedClientId, selectedClient, viewMode]);

  // Helper function to get appropriate quantity based on client context
  const getItemQuantity = useCallback((item) => {
    if (!selectedClientId || selectedClientId === 'global') {
      return item.remaining_quantity || 0;
    }

    // NEW: Flattened API returns client_quantity directly (location-based records)
    if (typeof item.client_quantity !== 'undefined' && item.client_quantity !== null) {
      return item.client_quantity || 0;
    }

    // OLD: Fallback for nested client inventory (backward compatibility)
    if (item.client_inventory?.[selectedClientId]) {
      return item.client_inventory[selectedClientId].remaining_quantity || 0;
    }

    return 0; // No inventory for this client
  }, [selectedClientId]);

  // Helper function to get location based on client context
  const getItemLocation = useCallback((item) => {
    if (!selectedClientId || selectedClientId === 'global') {
      return item.location || '-';
    }

    // NEW: Flattened API returns location directly on each record
    // Each product-location combination is a separate row now
    if (item.location) {
      return item.location;
    }

    // OLD: Fallback for nested client inventory (backward compatibility)
    if (item.client_inventory?.[selectedClientId]) {
      return item.client_inventory[selectedClientId].location || '-';
    }

    return '-';
  }, [selectedClientId]);

  // Mobile view
  if (isMobile) {
    return (
      <div>
        {/* Mobile header with sort info */}
        <div
          className="flex justify-between items-center mb-4 p-4 bg-muted rounded border border-gray-200"
        >
          <span className="text-sm text-muted-foreground">
            排序方式: {headCells.find(h => h.id === sort.by)?.label || '名称'} ({sort.order === 'asc' ? '升序' : '降序'})
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onSort(sort.by)}
          >
            <ArrowUpDown className="h-4 w-4 mr-1" />
            Toggle Sort
          </Button>
        </div>

        {/* Mobile cards */}
        <div className="space-y-0" role="list" aria-label="Inventory items">
          {inventory.map((item, index) => (
            <div key={item._id ? String(item._id) : `item-${index}`} role="listitem">
              <InventoryMobileCard
                item={item}
                selectedClientId={selectedClientId}
                viewMode={viewMode}
                onViewHistory={() => onViewHistory?.(item)}
              />
            </div>
          ))}
        </div>

        {/* Empty state for mobile */}
        {inventory.length === 0 && (
          <Card className="p-8 text-center mt-4">
            <p className="text-muted-foreground mb-4">
              {debouncedGlobalSearch?.trim()
                ? `No inventory items found for "${debouncedGlobalSearch}"`
                : 'No inventory items found'
              }
            </p>
            <Button
              size="sm"
              onClick={onClearAllFilters}
            >
              Clear all filters
            </Button>
          </Card>
        )}
      </div>
    );
  }

  // Desktop table view (read-only)
  return (
    <div>
      <Card className="shadow-none">
        <div className="overflow-auto">
          <table className="w-full" aria-label="Inventory table (read-only)">
            <thead className="bg-muted sticky top-0">
              <tr>
                {headCells.map((headCell) => (
                  <th
                    key={headCell.id}
                    className={`px-4 py-3 text-left text-sm font-semibold text-foreground ${
                      headCell.width ? `w-[${headCell.width}px]` : ''
                    }`}
                  >
                    {headCell.sortable ? (
                      <button
                        className="flex items-center gap-1 hover:text-primary"
                        onClick={() => onSort(headCell.id)}
                      >
                        {headCell.label}
                        <ArrowUpDown className={`h-4 w-4 ${sort.by === headCell.id ? 'text-primary' : 'text-muted-foreground'}`} />
                      </button>
                    ) : (
                      headCell.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {inventory.map((item, index) => {
                const itemId = item._id || item.product_id || `item-${index}`;

                return (
                  <tr
                    key={itemId}
                    className="hover:bg-muted"
                  >
                    {/* Item name */}
                    <td className="px-4 py-3" id={`item-${index}-name`}>
                      <span className="text-sm font-medium">
                        {item.name || 'Unnamed Item'}
                      </span>
                    </td>

                    {/* Product ID */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-muted-foreground">
                        {item.product_id || item.product_code || '-'}
                      </span>
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3">
                      <span className="text-sm">
                        {item.category || '-'}
                      </span>
                    </td>

                    {/* Specification */}
                    <td className="px-4 py-3">
                      <span className="text-sm">
                        {item.specification || '-'}
                      </span>
                    </td>

                    {/* Client Name (global view only) */}
                    {(!selectedClientId || selectedClientId === 'global') && (
                      <td className="px-4 py-3">
                        <span className="text-sm">
                          {item.client_name || '-'}
                        </span>
                      </td>
                    )}

                    {/* Quantity with inline reserved indicator */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-medium">
                          {getItemQuantity(item)}
                        </span>
                        {item.reservation_info?.total_reserved > 0 && (
                          <ReservedStockIndicator
                            reservedQuantity={item.reservation_info.total_reserved}
                            reservationDetails={item.reservation_info?.details || []}
                            size="small"
                            inline={true}
                          />
                        )}
                      </div>
                    </td>

                    {/* Leftovers */}
                    <td className="px-4 py-3">
                      {item.leftover_count > 0 ? (
                        <LeftoverIndicator
                          productId={item.product_id}
                          leftoverCount={item.leftover_count}
                          leftoverTotalQuantity={item.leftover_total_quantity}
                        />
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </td>

                    {/* Purchase pending quantity (raw material only) */}
                    {viewMode === 'raw_material' && (
                      <td className="px-4 py-3">
                        {item.purchase_pending_quantity > 0 && item.purchase_pending_po_ids?.length > 0 ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-sm font-medium text-amber-600 cursor-help border-b border-dashed border-amber-600">
                                  {item.purchase_pending_quantity}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                <div className="space-y-1">
                                  <p className="text-xs font-medium text-muted-foreground">未收采购单</p>
                                  <div className="flex flex-wrap gap-1">
                                    {item.purchase_pending_po_ids.map((poId) => (
                                      <span
                                        key={poId}
                                        className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded"
                                      >
                                        {poId}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className={`text-sm font-medium ${item.purchase_pending_quantity > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                            {item.purchase_pending_quantity > 0 ? item.purchase_pending_quantity : '-'}
                          </span>
                        )}
                      </td>
                    )}

                    {/* Tip Diameter */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-muted-foreground">
                        {item.tip_diameter || '-'}
                      </span>
                    </td>

                    {/* Location */}
                    <td className="px-4 py-3">
                      <span className="text-sm">
                        {getItemLocation(item)}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div
                          className={`w-2 h-2 rounded-full mr-2 ${
                            getItemQuantity(item) > 0 ? 'bg-success/100' : 'bg-destructive/100'
                          }`}
                        />
                        <span className="text-sm text-muted-foreground">
                          {getItemQuantity(item) > 0 ? '有库存' : '无库存'}
                        </span>
                      </div>
                    </td>


                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onViewHistory?.(item)}
                          className="min-w-[60px]"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          历史
                        </Button>
                        {(item.category === '原材料' || item.product_type === 'raw_material') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onViewLeftovers?.(item)}
                            className="min-w-[60px]"
                          >
                            <Recycle className="h-4 w-4 mr-1" />
                            余料
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {/* Empty state for desktop */}
              {inventory.length === 0 && (
                <tr>
                  <td colSpan={headCells.length} className="px-4 py-12 text-center">
                    <p className="text-muted-foreground mb-4">
                      {debouncedGlobalSearch?.trim()
                        ? `No inventory items found for "${debouncedGlobalSearch}"`
                        : 'No inventory items found'
                      }
                    </p>
                    <Button size="sm" onClick={onClearAllFilters}>
                      Clear all filters
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
});

InventoryList.displayName = 'InventoryList';

export default InventoryList;
